"use client";

/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/**
 * Report firing of specific actions to analytics endpoint.
 */
import _cloneDeep from "lodash/cloneDeep";
import Cookie from "js-cookie";
import QueryString from "query-string";
import { AnyObject, API, getCustomDomainComponents } from "@thenounproject/lingo-core";
import { downloadFile } from "@actions/useDownloadFile";
import { Action, UnknownAction } from "redux";
import { NavPoint } from "@redux/navPoint/NavPoint";
import { trackColorUsage } from "@redux/actions/analytics/trackColorUsage";
import { signup } from "@redux/actions/auth/useSignup";
import { login } from "@redux/actions/auth/useLogin";
import { fetchCurrentUser } from "@actions/users/useCurrentUser";
import { downloadMutlipleAssets } from "@redux/actions/useDownloadMultipleAssets";
import { updateSubscription } from "@redux/actions/billing/useUpdateSubscription";
import { captureException } from "../../adapters/sentry";
import { generateFilecut } from "@redux/actions/useGenerateFilecut";
import { showModal } from "@redux/actions/useModals";
import { createSupportRequest } from "@redux/actions/billing/useCreateSupportRequest";
import { requestEnterpriseSubscription } from "@redux/actions/billing/useRequestEnterpriseSubscription";
import { RootState } from "@redux/store";
import { hydrateStore } from "@actions/useHydrateStore";

// Middlewares aren't always called when loading the marketing site
// but we want to store referrer data before they are removed from the url.
storeReferrerData();

// Map action type to event name
function getInternalEvent(action: UnknownAction, navPoint: NavPoint) {
  if (generateFilecut.fulfilled.match(action)) {
    const {
      inspectable: {
        asset: { shareToken },
        item: { id: itemId, version } = {},
      },
      fileType,
      dimensions,
      userAction,
    } = action.meta.arg;

    return {
      event_name: "used_asset",
      data: {
        item_uuid: itemId,
        version: version,
        dimensions: dimensions,
        type: fileType,
        space_id: navPoint.spaceId,
        portal_uuid: navPoint.portalId,
        asset_token: shareToken,
        user_action: userAction,
      },
    };
  } else if (downloadMutlipleAssets.fulfilled.match(action)) {
    return action.meta.arg.inspectables
      .map(i => i.item)
      .filter(Boolean)
      .map(i => ({
        event_name: "used_asset",
        data: {
          item_uuid: i.id,
          version: i.version,
          space_id: navPoint.spaceId,
          portal_uuid: navPoint.portalId,
          asset_token: i.asset ? i.asset.shareToken : null,
        },
      }));
  } else if (trackColorUsage.match(action)) {
    const { item, format } = action.payload;
    return {
      event_name: "used_asset",
      data: {
        item_uuid: item.id,
        version: item.version,
        format: format,
        space_id: navPoint.spaceId,
        portal_uuid: navPoint.portalId,
        asset_token: item.asset?.shareToken,
      },
    };
  } else if (hydrateStore.match(action)) {
    const { spaceId, kitId, portalId, itemId, assetId, version, assetToken } =
        action.payload.navPoint,
      prevNavPoint = navPoint;

    if (!spaceId) return;
    if (
      prevNavPoint &&
      prevNavPoint.spaceId === spaceId &&
      prevNavPoint.kitId === kitId &&
      prevNavPoint.portalId === portalId &&
      prevNavPoint.itemId === itemId &&
      prevNavPoint.assetId === assetId
    )
      return;

    let event = "viewed_space";
    if (itemId || assetId) {
      event = "viewed_asset";
    } else if (kitId) {
      event = "viewed_kit";
    } else if (portalId) {
      event = "viewed_portal";
    }
    return {
      event_name: event,
      data: {
        space_id: spaceId,
        kit_uuid: kitId,
        portal_uuid: portalId,
        item_uuid: itemId,
        asset_uuid: assetId,
        asset_token: assetToken,
        version: kitId || itemId ? version : undefined,
      },
    };
  }
}

const gtmEvents: string[] = [
  downloadFile.type,
  signup.fulfilled.type,
  login.fulfilled.type,
  updateSubscription.fulfilled.type,
  generateFilecut.fulfilled.type,
  showModal.type,
  createSupportRequest.fulfilled.type,
  requestEnterpriseSubscription.fulfilled.type,
];
function actionToGtmEvent(action: UnknownAction, _navPoint: NavPoint) {
  if (!gtmEvents.includes(action.type)) return null;
  const clonedAction = _cloneDeep(action) as AnyObject;
  // Remove some data from the action
  clonedAction.response = undefined;
  if (typeof clonedAction.payload === "object") {
    clonedAction.payload.result = undefined;
    clonedAction.payload.entities = undefined;
  }
  const { type, ...actionData } = clonedAction;
  return { event: "Redux Action", type, actionData };
}

const analytics = store => next => (action: Action) => {
  storeReferrerData();
  const state = store.getState() as RootState,
    navPoint = state.navPoint;
  processAnalytics(action, navPoint || ({} as NavPoint));
  processIntercomEvent(action, state);
  processClarityEvent(action, state);
  return next(action);
};

// Store any utms in cookies for use any potential signup later
function storeReferrerData() {
  if (typeof window === "undefined") {
    return;
  }
  const domain = getCustomDomainComponents().host ?? "lingoapp.com";

  const qs = QueryString.parse(window.location.search);
  Object.keys(qs).forEach(k => {
    if (k.startsWith("utm")) {
      Cookie.set(k, qs[k] as string, { domain });
    }
  });

  // Source data includes referrer and other known attributes
  // source_referrer: referrer
  // -- Google specific data --
  // source_ga_keyword: The keyword that was searched for
  // source_ga_campaign: The campaign name
  // source_ga_content: content?
  // source_ga_source: source?
  // source_ga_gclid: the click id that should get reported back to google
  function setSource(key: string, value: string) {
    if (!value) return;
    const _key = `source_${key}`;
    if (Cookie.get(_key)) return;
    Cookie.set(_key, value, { domain });
  }

  if (qs.msclkid) {
    setSource("referrer", "https://www.bing.com/");
    setSource("ga_gclid", qs.msclkid as string);
    setSource("ga_keyword", qs.keyword as string);
    setSource("ga_campaign", qs.campaign as string);
    setSource("ga_adgroup", qs.adgroup as string);
  } else if (qs.gad_source) {
    setSource("referrer", "https://www.google.com/");
    setSource("ga_keyword", qs.keyword as string);
    setSource("ga_campaign", qs.campaign as string);
    setSource("ga_content", qs.content as string);
    setSource("ga_source", qs.gad_source as string);
    setSource("ga_gclid", qs.gclid as string);
  } else {
    setSource("referrer", document.referrer);
  }
}

function processAnalytics(action: Action, navPoint: NavPoint) {
  const internalEvent = getInternalEvent(action, navPoint),
    gtmEvent = actionToGtmEvent(action, navPoint);

  if (internalEvent) void sendEventToLingo(internalEvent);
  if (gtmEvent) sendEventToGTM(gtmEvent);
}

function sendEventToGTM(data) {
  window.dataLayer?.push?.(data);
}

async function sendEventToLingo(event) {
  if (!event) return;
  const events = Array.isArray(event) ? event : [event];
  if (!events.length) return;
  try {
    await API.call({
      endpoint: "analytics/events/",
      method: "POST",
      data: {
        events,
      },
    });
  } catch (err) {
    captureException(new Error(err.message ?? err));
  }
}

function processIntercomEvent(action: Action, state: RootState) {
  if (!window.Intercom) return;

  const updates: AnyObject = {};
  if (
    signup.fulfilled.match(action) ||
    login.fulfilled.match(action) ||
    fetchCurrentUser.fulfilled.match(action)
  ) {
    const { user: userId, intercomId } = action.payload.result,
      user = action.payload.entities.users[userId];

    updates.user_hash = intercomId;
    updates.name = user.name;
    updates.email = user.email;
    updates.user_id = String(user.id);
  } else if (hydrateStore.match(action)) {
    const navPoint = action.payload.navPoint;
    if (!navPoint) return;
    const {
        entities: { spaces, kits },
      } = state,
      { kitId, spaceId } = navPoint,
      space = spaceId ? spaces.objects[spaceId] : null,
      spaceRole = space && space.access ? space.access.role : null,
      kit = kitId ? kits.objects[kitId] : null,
      kitRole = kit && kit.access ? kit.access.role : null;
    updates.active_space_role = spaceRole;
    updates.active_kit_role = kitRole;
  }
  if (Object.keys(updates).length) {
    window.Intercom?.("update", updates);
  }
}

function processClarityEvent(action: Action, state: RootState) {
  if (state.user?.id) {
    window.clarity?.("set", "logged_in", true);
  }
}

export default analytics;
